import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";
import IconButton from "../../components/common/IconButton";
import { MdDelete } from "react-icons/md";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { MEAL_PLAN, ROOM_RATE_TYPE } from "../../_contstants/common";
import { useLocation } from "react-router-dom";

const RoomPricingArray = (fieldArrayRenderProps: FieldArrayRenderProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const PackageID = location.state?.PackageID;
  const [deleteIndex, setDeleteIndex] = useState(-1);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleOpenDeleteDialog = (index: number) => {
    dispatch(openDialog("deleteRoomPricing"));
    setDeleteIndex(index);
  };
  const handleDeleteAction = useCallback(
    (index: number) => {
      fieldArrayRenderProps.onRemove({ index: index });
      dispatch(closeDialog());
      setDeleteIndex(-1);
    },
    [fieldArrayRenderProps],
  );
  const pushElementInarray = useCallback(() => {
    fieldArrayRenderProps.onPush({
      value: {
        RoomCategory: "",
        NoOfRooms: "",
        Meal: "",
        OneNightRate: 0,
        OneNightRateType: "Per Couple",
        TwoNightRate: 0,
        TwoNightRateType: "Per Couple",
      },
    });
  }, [fieldArrayRenderProps]);

  useEffect(() => {
    if (!PackageID && !fieldArrayRenderProps?.value?.length) {
      pushElementInarray();
    }
  }, []);

  return (
    <>
      <GridLayoutItem
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Typography.h4>Add Room Pricing</Typography.h4>
        <div>
          <Button
            fillMode={"solid"}
            themeColor={"primary"}
            onClick={pushElementInarray}
            type="button"
          >
            Add New
          </Button>
        </div>
      </GridLayoutItem>

      {dialogName === "deleteRoomPricing" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Room Pricing"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Room Pricing?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(deleteIndex)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 &&
        fieldArrayRenderProps.value?.map(
          (Roompricingdetails: any, index: number) => (
            <GridLayoutItem
              style={{
                border: "1px solid orange",
                borderRadius: 8,
                marginBottom: 10,
              }}
            >
              <GridLayout
                key={index}
                style={{
                  marginRight: 30,
                  padding: "0 20px 20px 20px",
                }}
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem>
                  <Field
                    name={`Roompricingdetails.${index}.RoomCategory`}
                    label="Room Category"
                    placeholder="Room Category"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                    wrapperClassName="w-100"
                    maxLength={150}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`Roompricingdetails.${index}.NoOfRooms`}
                    label="No. Of Rooms"
                    format="n0"
                    placeholder="0"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`Roompricingdetails.${index}.Meal`}
                    label="Meal"
                    placeholder="Meal"
                    validator={requiredValidator}
                    astrike={true}
                    rows={2}
                    component={FormSelectionField}
                    options={MEAL_PLAN?.map((meal: any) => {
                      return {
                        value: meal?.value,
                        label: meal?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ textAlign: "end" }}>
                  <IconButton
                    onClick={() => handleOpenDeleteDialog(index)}
                    themeColor={"error"}
                    size={"small"}
                    fillMode={"solid"}
                    type="button"
                    style={{
                      marginTop: 35,
                      height: 30,
                      width: 30,
                    }}
                    icon={MdDelete}
                    iconClassName="absolute-position"
                    iconStyle={{ fontSize: "24px" }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`Roompricingdetails.${index}.OneNightRate`}
                    label="1 Night Rate"
                    format="n0"
                    placeholder="0"
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`Roompricingdetails.${index}.OneNightRateType`}
                    label="1 Night Rate Type"
                    placeholder="Rate Type"
                    validator={requiredValidator}
                    astrike={true}
                    rows={2}
                    component={FormSelectionField}
                    options={ROOM_RATE_TYPE?.map((ratetype: any) => {
                      return {
                        value: ratetype?.value,
                        label: ratetype?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name={`Roompricingdetails.${index}.TwoNightRate`}
                    label="2 Night Rate"
                    format="n0"
                    placeholder="0"
                    component={FormNumericTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`Roompricingdetails.${index}.TwoNightRateType`}
                    label="2 Night Rate Type"
                    placeholder="Rate Type"
                    validator={requiredValidator}
                    astrike={true}
                    rows={2}
                    component={FormSelectionField}
                    options={ROOM_RATE_TYPE?.map((ratetype: any) => {
                      return {
                        value: ratetype?.value,
                        label: ratetype?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
              </GridLayout>
            </GridLayoutItem>
          ),
        )}
    </>
  );
};

export default RoomPricingArray;
